// src/modules/livro-ordens/OrderPostForm.js
import React, { useState, useEffect } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const XIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>;
const InfoIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
const ClockIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
const AlertCircleIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;
const CheckCircleIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>;

const CATEGORIES = [
  { value: 'info', label: 'Informação', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: <InfoIcon /> },
  { value: 'ordem', label: 'Ordem', color: 'bg-red-50 text-red-700 border-red-200', icon: <ClockIcon /> },
  { value: 'urgente', label: 'Urgente', color: 'bg-rose-50 text-rose-700 border-rose-200', icon: <AlertCircleIcon /> },
  { value: 'tarefa', label: 'Tarefa', color: 'bg-amber-50 text-amber-700 border-amber-200', icon: <CheckCircleIcon /> },
];

export default function OrderPostForm({ isOpen, onClose, ordemEditando }) {
  const { userProfile } = useAuth();
  
  const [formData, setFormData] = useState({
    numeracao: '',
    data_ordem: '',
    validade: '',
    emanada_por: '',
    local_meio: '',
    transmitida_por: '',
    conteudo: ''
  });
  
  const [newCategory, setNewCategory] = useState('ordem');
  const [loading, setLoading] = useState(false);

  // Efeito para preencher o formulário quando for modo de edição
  useEffect(() => {
    if (ordemEditando) {
      setFormData({
        numeracao: ordemEditando.numeracao || '',
        data_ordem: ordemEditando.data_ordem || '',
        validade: ordemEditando.validade || '',
        emanada_por: ordemEditando.emanada_por || '',
        local_meio: ordemEditando.local_meio || '',
        transmitida_por: ordemEditando.transmitida_por || '',
        conteudo: ordemEditando.conteudo || ''
      });
      setNewCategory(ordemEditando.categoria || 'ordem');
    } else {
      setFormData({
        numeracao: '', data_ordem: '', validade: '', emanada_por: '', local_meio: '', transmitida_por: '', conteudo: ''
      });
      setNewCategory('ordem');
    }
  }, [ordemEditando, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.conteudo.trim() || !formData.numeracao.trim()) {
        alert("Preencha ao menos a Numeração e o Conteúdo da ordem.");
        return;
    }
    
    setLoading(true);
    try {
      if (ordemEditando) {
        // Atualizar ordem existente
        const docRef = doc(db, paths.livro_ordens, ordemEditando.id);
        await updateDoc(docRef, {
          numeracao: formData.numeracao,
          data_ordem: formData.data_ordem,
          validade: formData.validade,
          emanada_por: formData.emanada_por,
          local_meio: formData.local_meio,
          transmitida_por: formData.transmitida_por,
          conteudo: formData.conteudo,
          categoria: newCategory,
        });
      } else {
        // Criar nova ordem
        await addDoc(collection(db, paths.livro_ordens), {
          numeracao: formData.numeracao,
          data_ordem: formData.data_ordem,
          validade: formData.validade,
          emanada_por: formData.emanada_por,
          local_meio: formData.local_meio,
          transmitida_por: formData.transmitida_por,
          conteudo: formData.conteudo,
          categoria: newCategory,
          autor_nome: userProfile.nome,
          autor_id: userProfile.id,
          criado_em: serverTimestamp()
        });
      }
      
      onClose();
    } catch (err) {
      console.error("Erro ao salvar ordem:", err);
      alert("Falha ao salvar a ordem. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
        
        {/* Header Fixo do Modal */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50 rounded-t-xl shrink-0">
          <h2 className="text-lg font-bold text-slate-800 tracking-tight">
            {ordemEditando ? 'Editar Publicação' : 'Nova Publicação'}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-200 rounded-md transition-colors text-slate-500">
            <XIcon />
          </button>
        </div>
        
        {/* Corpo do Formulário com Scroll */}
        <div className="overflow-y-auto p-5">
            <form id="ordemForm" onSubmit={handleSubmit} className="space-y-4">
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Numeração</label>
                <input 
                    name="numeracao" value={formData.numeracao} onChange={handleChange} required autoFocus={!ordemEditando}
                    placeholder="Ex: 001/2026"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm font-medium uppercase"
                />
                </div>
                <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Data</label>
                <input 
                    type="date" name="data_ordem" value={formData.data_ordem} onChange={handleChange} required
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm font-medium"
                />
                </div>
                <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Validade</label>
                <input 
                    type="text" name="validade" value={formData.validade} onChange={handleChange}
                    placeholder="Ex: Até 20/05/2026"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm font-medium uppercase"
                />
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Emanada Por</label>
                <input 
                    type="text" name="emanada_por" value={formData.emanada_por} onChange={handleChange} required
                    placeholder="Ex: Cmt APMBB"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm font-medium uppercase"
                />
                </div>
                <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Transmitida Por</label>
                <input 
                    type="text" name="transmitida_por" value={formData.transmitida_por} onChange={handleChange} required
                    placeholder="Quem está repassando a ordem"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm font-medium uppercase"
                />
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Local ou Meio Usado</label>
                <input 
                    type="text" name="local_meio" value={formData.local_meio} onChange={handleChange} required
                    placeholder="Ex: Pátio de Formatura / WhatsApp"
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-sm font-medium uppercase"
                />
            </div>
            
            <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Categoria / Classificação</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {CATEGORIES.map(cat => (
                    <button
                    key={cat.value} type="button" onClick={() => setNewCategory(cat.value)}
                    className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md border text-xs font-bold transition-all ${
                        newCategory === cat.value ? `${cat.color} ring-1 ring-current` : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                    >
                    {cat.icon}
                    {cat.label}
                    </button>
                ))}
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Ordem / Mensagem / Orientação</label>
                <textarea 
                required name="conteudo" rows={6} value={formData.conteudo} onChange={handleChange}
                placeholder="Descreva as informações aqui..."
                className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all resize-none text-sm"
                />
            </div>
            </form>
        </div>

        {/* Rodapé Fixo do Modal */}
        <div className="p-4 border-t border-slate-100 bg-slate-50 rounded-b-xl flex gap-2 shrink-0">
            <button 
                type="button" onClick={onClose}
                className="flex-1 px-3 py-2.5 rounded-lg border border-slate-200 text-slate-600 font-bold hover:bg-slate-100 transition-all text-sm"
            >
                Cancelar
            </button>
            <button 
                type="submit" form="ordemForm" disabled={loading}
                className="flex-1 px-3 py-2.5 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all shadow-sm active:scale-[0.98] disabled:opacity-50 text-sm"
            >
                {loading ? 'Salvando...' : (ordemEditando ? 'Salvar Alterações' : 'Publicar no Mural')}
            </button>
        </div>

      </div>
    </div>
  );
}